import type { Maybe } from '@amazon/vinyl-util'
import type { ImageFields } from '@/service/fragments/imageFieldsFrag'
import type { PodcastEpisodeFields } from '@/service/fragments/podcastEpisodeFieldsFrag'
import { getLargestImage } from '@/util/imageUtil'

type VideoStreamingFormat = NonNullable<
    PodcastEpisodeFields['playbackInformation']
>['videoStreamingFormat']

export interface PodcastEpisodeVideoInfo {
    /** True when Firefly resolved a playable video manifest URL. */
    readonly hasVideo: boolean
    readonly videoUrl: string | null
    readonly videoStreamingFormat: VideoStreamingFormat | null
    /** Landscape 16:9 video artwork, when the episode carries one. */
    readonly videoThumbnail: ImageFields | null
}

/**
 * Single source of truth for a podcast episode's video presence + assets.
 * Video-presence logic lives here so browse / search / t1r surfaces read it
 * one way rather than each re-inlining a `videoUrl != null` check.
 */
export function getPodcastEpisodeVideoInfo(
    episode: Maybe<PodcastEpisodeFields>
): PodcastEpisodeVideoInfo {
    const pb = episode?.playbackInformation
    const videoUrl = pb?.videoUrl ?? null
    return {
        hasVideo: videoUrl != null,
        videoUrl,
        videoStreamingFormat: pb?.videoStreamingFormat ?? null,
        videoThumbnail: getLargestImage(episode?.images, 'VIDEO_THUMBNAIL'),
    }
}
